import {Trans} from '@lingui/macro';
import React, {FC, useEffect, useState} from 'react';
import styled from 'styled-components';

import {useInitialEffect} from '../../../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {HandlerOf} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {useLinkNavigation} from '../../../../../../components/common/navigation/useLinkNavigation';
import {isIntegrationSettingFieldLoading} from '../../../../../../helpers/integrationHelpers';
import {useAppDispatch, useAppSelector} from '../../../../../../helpers/reduxHelpers';
import {
  FullcontactIntegrationModel,
  FullcontactIntegrationSettingsModel,
} from '../../../../../../models/api/integrationModel';
import {decryptIntegrationSettingsAsyncAction} from '../../../../../../store/data/integrations/integrationsActions';
import {getIsUserGlobalAdmin} from '../../../../../../store/data/teammates/teammatesSelectors';
import {maybeRenderConfigureDialogTextBox} from '../shared/settingsConfigureHelpers';

/*
 * Constants.
 */

const seeHowItWorksLink =
  'http://help.frontapp.com/t/x16pzc/how-to-enable-and-use-the-fullcontact-integration';

/*
 * Props.
 */

interface SettingsAppsConfigureFullcontactProps {
  integration?: FullcontactIntegrationModel;
  configurationSettings?: FullcontactIntegrationSettingsModel;
  onIsSaveEnabled: HandlerOf<boolean>;
  setConfigurationSettings: HandlerOf<FullcontactIntegrationSettingsModel | undefined>;
}

/*
 * Style.
 */

const StyledSeeHowItWorksWrapperDiv = styled.div`
  display: flex;
  margin-left: -14px;
  margin-bottom: 16px;
`;

/*
 * Component.
 */

export const SettingsAppsConfigureFullcontact: FC<SettingsAppsConfigureFullcontactProps> = (props) => {
  const {configurationSettings, integration, onIsSaveEnabled, setConfigurationSettings} = props;
  const isGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);
  const settingsApiKey = configurationSettings?.apiKey || '';
  const {navigateToLink} = useLinkNavigation();
  const dispatch = useAppDispatch();

  const [isDecrypting, setIsDecrypting] = useState(
    isGlobalAdmin && isIntegrationSettingFieldLoading(settingsApiKey),
  );

  // When we first load the settings, we may need to decrypt the settings.
  useInitialEffect(() => {
    if (!integration) {
      return;
    }
    dispatch(decryptIntegrationSettingsAsyncAction(integration));
  });

  // Controls if save is enabled for this dialog.
  //  This requires the api-key to be filled out, but there is no validation.
  useEffect(() => {
    if (!configurationSettings) {
      onIsSaveEnabled(false);
      return;
    }
    const {apiKey} = configurationSettings;
    onIsSaveEnabled(Boolean(apiKey && apiKey.trim() !== '' && !isIntegrationSettingFieldLoading(apiKey)));
  }, [configurationSettings, onIsSaveEnabled]);

  // Controls if we are still decrypting or not.
  useEffect(() => {
    if (isDecrypting && !isIntegrationSettingFieldLoading(settingsApiKey)) {
      setIsDecrypting(false);
      setConfigurationSettings({apiKey: settingsApiKey});
    }
  }, [settingsApiKey, isDecrypting, setConfigurationSettings]);

  // Simple handler for setting the correct settings to save.
  const onSetApiKey = (apiKey: string) => {
    setConfigurationSettings({apiKey});
  };

  return (
    <>
      <Trans id="settings-apps-configure-app-dialog-fullcontact-description">
        Display contact information alongside conversations.
      </Trans>
      <StyledSeeHowItWorksWrapperDiv>
        <Button variant={ButtonVariantsEnum.LINK} onClick={() => navigateToLink(seeHowItWorksLink)}>
          <Trans id="settings-apps-configure-app-dialog-fullcontact-see-how-it-works">See how it works</Trans>
        </Button>
      </StyledSeeHowItWorksWrapperDiv>
      {maybeRenderConfigureDialogTextBox(
        configurationSettings?.apiKey || '',
        <Trans id="settings-apps-configure-app-dialog-fullcontact-api-key">API Key</Trans>,
        onSetApiKey,
        {
          isDisabled: !isGlobalAdmin,
          isLoading: isDecrypting,
          isRequired: true,
        },
      )}
    </>
  );
};
